import { Map, Layers, FileSearch, Shield, Train, Building2, TrendingUp, Landmark, Users, Handshake, MapPin } from 'lucide-react';

export const developmentInsightsArticles = [
  // ═══════════════════════════════════════════
  // ZONING EXPLAINER SERIES (4 articles)
  // ═══════════════════════════════════════════
  {
    id: 'what-does-low-medium-density-zoning-mean',
    category: 'Seller Guide',
    title: 'What Does Low-Medium Density Zoning Mean for My Property?',
    excerpt: 'If your property is zoned LMR or Low-Medium Density Residential, it may have significantly more development potential than you realise.',
    date: '2026-04-12',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=85',
    icon: Map,
    metaDescription: 'What Low-Medium Density Residential zoning means for your property in Brisbane. Permitted uses, development potential, minimum lot sizes, and how this zoning increases land value.',
    content: `
## Low-Medium Density Residential: The Sweet Spot for Development

If your property is zoned Low-Medium Density Residential (LMDR) in Brisbane or a similar zone in other SEQ councils, you are sitting in the development sweet spot. This zoning permits a range of housing types that make your land attractive to developers, owner-builders, and investors.

## What Can Be Built on LMDR Land?

Under Brisbane City Plan 2014, Low-Medium Density Residential permits:

| Development Type | Typical Yield | Minimum Lot Size |
|---|---|---|
| Detached house | 1 dwelling | 300m² |
| Dual occupancy | 2 dwellings | 400m² |
| Townhouses | 3-6 dwellings | 600m²+ |
| Multiple dwelling (units) | Subject to site | 800m²+ |

The key difference from Low Density Residential (LDR) is the **smaller minimum lot size** (300m² vs 400m²) and the ability to build **attached dwellings** like townhouses without a complex impact assessment.

### What This Means in Practice

A 900m² LMDR block in Greenslopes could potentially yield:
- 3 x 300m² lots (subdivision only)
- 3 townhouses (built form development)
- A duplex plus a detached house

Compare this to the same block in LDR, which might only yield 2 x 450m² lots. The additional yield directly translates to higher land value.

## How LMDR Zoning Affects Your Property Value

Developers calculate land value based on what they can build. More dwellings = higher revenue = higher land price they can pay you.

| Block Size | LDR Value (2 lots) | LMDR Value (3 lots) | Premium |
|---|---|---|---|
| 800m² in Coorparoo | $800,000 | $1,050,000 | +31% |
| 900m² in Chermside | $720,000 | $950,000 | +32% |
| 1,000m² in Holland Park | $850,000 | $1,100,000 | +29% |

These figures are indicative — actual values depend on many factors. But the LMDR premium is real and consistent.

## How to Check If You Are LMDR

1. Visit your council's online mapping tool
2. Enter your address
3. Look for the zone code — in Brisbane, LMDR appears as a distinct colour on the zoning map
4. Check for any overlays that might restrict development

In Brisbane: [Brisbane City Plan Interactive Mapping](https://cityplan.brisbane.qld.gov.au)

## LMDR in Other SEQ Councils

Different councils use different names for similar zones:

| Council | Equivalent Zone Name |
|---|---|
| Brisbane | Low-Medium Density Residential |
| Logan | Low-Medium Density Residential |
| Gold Coast | Low-Medium Density Residential |
| Moreton Bay | Medium Density Residential |
| Ipswich | Medium Density Residential |

The rules vary by council, so always check the specific planning scheme.

## Common Questions from LMDR Property Owners

### Do I need approval to develop?

Yes. Any new development beyond a single house requires a Development Application. However, in LMDR zones, many developments are **code assessable** (assessed against rules, no public notification), making the approval process faster and more predictable.

### Can I subdivide without building?

Yes. You can subdivide your block into smaller lots and sell them as vacant land. Buyers then build their own homes. This is often the simplest and lowest-risk approach.

### Will council definitely approve my development?

If your proposal meets the planning scheme requirements (lot sizes, setbacks, height, parking), code assessable applications are very likely to be approved. Non-compliances trigger impact assessment, which is less certain.

## What Should You Do?

If your property is zoned LMDR and your block is over 600m², you have genuine development potential. The first step is understanding exactly what can be built and what the land is worth to a developer.

[Request a free development potential assessment](/contact) from our advisory team. We will review your zoning, overlays, and site characteristics and give you a clear picture of your options.
`,
    faq: [
      { q: 'What is Low-Medium Density Residential zoning?', a: 'Low-Medium Density Residential (LMDR) is a planning zone that permits a range of housing types including detached houses, duplexes, townhouses, and small unit developments. Minimum lot sizes are typically smaller than Low Density zones, allowing more dwellings per site.' },
      { q: 'Is my property worth more if it is zoned LMDR?', a: 'Generally yes. LMDR zoning permits higher density development than Low Density zones, which means developers can build more dwellings and therefore pay more for the land. The premium is typically 20-35% above equivalent LDR properties.' },
      { q: 'Can I build townhouses on LMDR land?', a: 'Yes. Townhouses are a permitted use in LMDR zones subject to a Development Application. The number of townhouses depends on your lot size, site dimensions, and compliance with the planning scheme requirements for setbacks, height, and parking.' },
    ],
  },
  {
    id: 'medium-density-residential-explained',
    category: 'Seller Guide',
    title: 'Medium Density Residential Explained: What Can Be Built on Your Block?',
    excerpt: 'Medium Density zoning opens the door to significant development. Here is what it means for your property and its value.',
    date: '2026-04-11',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=85',
    icon: Building2,
    metaDescription: 'What Medium Density Residential zoning means for your property. Permitted development types, height limits, yield potential, and how MDR zoning dramatically increases land value.',
    content: `
## Medium Density Residential: Maximum Development Potential

Medium Density Residential (MDR) is the highest-density residential zone in many SEQ councils. If your property carries this zoning, you are holding one of the most valuable residential land classifications available.

## What MDR Permits

In Brisbane City Council, Medium Density Residential allows:

- **Multiple dwellings (units/apartments)** — up to 3-4 storeys depending on the neighbourhood plan
- **Townhouses** — typically 4-12+ on a single site
- **Rooming accommodation** — student or worker housing
- **Retirement facilities** — aged care complexes
- **Short-term accommodation** — serviced apartments

There is **no minimum lot size** for MDR in Brisbane, meaning even smaller blocks can be developed at higher densities.

## Height and Density Limits

MDR height limits vary by neighbourhood plan:

| Area/Neighbourhood Plan | Max Height | Max Storeys |
|---|---|---|
| General MDR (no specific plan) | 12m | 3 storeys |
| Selected precincts near centres | 15m | 4 storeys |
| Specific transit-oriented precincts | 18-25m | 5-8 storeys |

Always check your specific neighbourhood plan — the height limit directly determines how many units can be built, which determines what a developer will pay.

## The Value Multiplier

MDR zoning creates a dramatic value multiplier compared to lower-density zones:

**Example: 1,000m² block in Nundah**
| Scenario | Dwellings | Gross Revenue | Land Value |
|---|---|---|---|
| Low Density (2 houses) | 2 | $1,600,000 | $650,000 |
| Low-Medium Density (4 townhouses) | 4 | $3,200,000 | $900,000 |
| Medium Density (8 units) | 8 | $5,600,000 | $1,400,000 |

The same block of land can be worth more than double under MDR zoning compared to LDR. This is why rezoning announcements cause immediate land value increases.

## Where Is MDR Zoning in Brisbane?

MDR zones are concentrated around:

- **Major centres:** Chermside, Indooroopilly, Toowong, Carindale
- **Train station precincts:** Within 400-800m of stations along all lines
- **Bus interchange areas:** Major busway stations
- **Activity centres:** Local shopping and mixed-use precincts
- **Growth corridors:** Along major transport routes

Suburbs with significant MDR land include Woolloongabba, Fortitude Valley, Newstead, Toowong, Indooroopilly, Chermside, and Nundah.

## What Makes MDR Land Valuable to Developers

Developers target MDR sites because:

1. **Higher yield** — more dwellings per site = more revenue
2. **Predictable approvals** — code assessable if compliant
3. **Strong end-buyer demand** — units near transport and amenities sell quickly
4. **Lower per-unit land cost** — spreading land cost over more dwellings improves feasibility

## Should You Sell or Develop?

### Sell to a Developer
- Clean, fast result
- Competitive process drives price
- No capital or risk required from you
- Best for: most MDR landowners

### Develop Yourself
- Potentially higher total return
- Requires significant capital ($1M+)
- 2-4 year timeline
- Best for: experienced, well-capitalised landowners

### Joint Venture
- You contribute land, developer contributes capital
- Split proceeds (typically 30-40% to landowner)
- Moderate risk, moderate return
- Best for: landowners who want upside without full capital commitment

## Get Your MDR Property Assessed

If your property is zoned Medium Density Residential, understanding its full development potential is essential — whether you plan to sell now or in 5 years. The value difference between a standard valuation and a development-aware assessment can be hundreds of thousands of dollars.

[Book a free MDR property assessment](/contact) with our advisory team.
`,
    faq: [
      { q: 'What is Medium Density Residential zoning?', a: 'Medium Density Residential (MDR) is a planning zone that permits higher-density housing including apartment buildings, large townhouse developments, and mixed-use projects. It is typically the highest-density residential zone in SEQ councils.' },
      { q: 'How much is MDR land worth per square metre?', a: 'MDR land in established Brisbane suburbs typically ranges from $1,500 to $4,000 per square metre, depending on location, height limits, and proximity to transport. Inner-city MDR land near train stations commands the highest prices.' },
      { q: 'Can I convert my property to Medium Density zoning?', a: 'You cannot change your zoning independently — zoning changes are made by council through planning scheme amendments. However, councils periodically rezone areas, particularly near transport infrastructure. If your suburb is being considered for rezoning, the announcement alone can increase your property value.' },
    ],
  },
  {
    id: 'how-to-read-council-zoning-map-5-minutes',
    category: 'Seller Guide',
    title: 'How to Read Your Council Zoning Map in 5 Minutes',
    excerpt: 'A quick, visual guide to understanding what your property zoning means and what development is possible.',
    date: '2026-04-10',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&q=85',
    icon: FileSearch,
    metaDescription: 'How to read your council zoning map in Brisbane and SEQ. Quick guide to finding your property zone, understanding zone codes, and checking development potential in 5 minutes.',
    content: `
## Your 5-Minute Zoning Check

Understanding your property's zoning is the single most important step in assessing its development potential. Here is how to do it in 5 minutes using free online tools.

## Step 1: Find Your Council's Mapping Tool (30 seconds)

| Council | Mapping Tool URL |
|---|---|
| Brisbane | cityplan.brisbane.qld.gov.au |
| Gold Coast | maps.goldcoast.qld.gov.au |
| Logan | logan.qld.gov.au/planning |
| Moreton Bay | moretonbay.qld.gov.au/planning |
| Ipswich | ipswich.qld.gov.au/planning |
| Sunshine Coast | sunshinecoast.qld.gov.au/planning |

## Step 2: Enter Your Address (30 seconds)

Type your address in the search bar. The map will zoom to your property and show its boundaries highlighted.

## Step 3: Read the Zone Code (1 minute)

Your property will be shaded a colour. Each colour represents a zone:

**Brisbane City Council colour guide:**
| Colour | Zone | Development Potential |
|---|---|---|
| Light yellow | Low Density Residential | Houses, basic subdivision |
| Medium yellow | Low-Medium Density | Houses, duplexes, townhouses |
| Orange | Medium Density Residential | Units, apartments, townhouses |
| Red/dark orange | High Density Residential | Tall apartments |
| Purple | Mixed Use | Residential + commercial |
| Light green | Community Facilities | Schools, parks, hospitals |
| Dark green | Conservation | Very limited development |

The warmer the colour (yellow → orange → red), the more development is permitted and the more your land is worth.

## Step 4: Check for Overlays (2 minutes)

Overlays are additional restrictions layered on top of the base zone. Common overlays that affect development:

### Flood Overlay
Shows areas prone to flooding. Development is still possible but with conditions (elevated floor levels, no habitable rooms on ground floor in severe flood areas).

### Character Overlay (Brisbane only)
Protects pre-1947 houses and streetscapes. Restricts demolition and requires sympathetic design for new buildings. Can limit development potential.

### Heritage Overlay
Protects specific buildings or places with heritage significance. Very restrictive — demolition usually not permitted.

### Vegetation Protection Overlay
Protects significant trees and vegetation. May restrict where you can build on your site.

### Waterway Corridor Overlay
Protects creeks and waterways. Creates setback requirements that reduce developable area.

To check overlays, look for an "Overlays" layer toggle in the mapping tool and enable each one to see if it affects your property.

## Step 5: Interpret What You Have Found (1 minute)

### Best Case for Development
- Medium Density or Low-Medium Density zoning
- No overlays or only minor overlays
- Block size significantly exceeds minimum lot requirements

### Good Potential
- Low Density Residential zoning
- Block over 800m²
- No restrictive overlays

### Limited Potential
- Conservation, Rural, or Character zoning
- Heritage overlay
- Significant flood overlay
- Block below minimum lot size

### Not Sure?
If you are uncertain what your zoning means for your property, [contact our team](/contact) for a free assessment. We review council maps every day and can interpret your specific situation in a quick phone call.

## Beyond the Zoning Map

The zoning map tells you what is permitted in theory. Other factors determine what is practical:

- **Lot size and dimensions** — a narrow lot may not fit the permitted density
- **Slope and topography** — steep sites cost more to develop
- **Access** — each lot or dwelling needs vehicle access
- **Services** — water, sewer, and stormwater capacity
- **Market demand** — what buyers actually want in your suburb

A professional assessment considers all of these factors alongside the zoning to give you an accurate picture of your property's development potential and value.
`,
    faq: [
      { q: 'Where do I find my property zoning?', a: 'Every SEQ council provides a free online mapping tool where you can search your address and see the zoning. Brisbane uses cityplan.brisbane.qld.gov.au. The zone appears as a colour overlay on the map with a code identifier.' },
      { q: 'What does the colour on the zoning map mean?', a: 'Each colour represents a different zone with different permitted uses. In general, lighter colours (yellow) indicate lower density residential, while warmer colours (orange, red) indicate higher density with more development potential.' },
      { q: 'What if my property has multiple overlays?', a: 'Overlays stack — each one adds conditions or restrictions. A property with flood AND character overlays faces more constraints than one with neither. However, overlays rarely prevent development entirely — they shape what is possible and add conditions.' },
    ],
  },
  {
    id: 'what-is-overlay-how-affects-property-value',
    category: 'Seller Guide',
    title: 'What Is an Overlay and How Does It Affect My Property Value?',
    excerpt: 'Planning overlays can increase or decrease your property value. Understanding yours is essential before selling.',
    date: '2026-04-09',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1460317442991-0ec209397118?w=800&q=85',
    icon: Layers,
    metaDescription: 'What planning overlays mean for your property value in Brisbane and SEQ. Flood, heritage, character, and vegetation overlays explained with their impact on development potential.',
    content: `
## Overlays: The Hidden Factor in Property Value

Your property's base zoning determines the general rules. But overlays add specific conditions that can significantly affect what you can build — and therefore what your land is worth.

Think of overlays as transparent sheets laid over the zoning map. Each sheet adds or restricts certain things.

## Types of Overlays and Their Impact

### Flood Overlay

**What it does:** Identifies properties in flood-prone areas based on historical data and modelling.

**Impact on development:**
- Ground floor habitable rooms may be prohibited in severe flood areas
- Floor levels must be above the defined flood level
- Additional stormwater management required
- Some development types may be restricted

**Impact on value:** Moderate negative. Flood overlays reduce the number of dwellings that can be built (no ground floor habitable rooms means fewer units). However, experienced developers can design around flood constraints. Value reduction is typically 10-25% compared to equivalent non-flood properties.

### Character Overlay (Brisbane)

**What it does:** Protects the character of pre-1947 residential areas.

**Impact on development:**
- Demolition of pre-1947 houses requires assessment
- New buildings must be sympathetic to the existing character
- Minimum lot sizes may be larger (450m² vs 400m²)
- Height and bulk restrictions apply

**Impact on value:** Mixed. Character restrictions limit what can be built, reducing developer interest. But character suburbs (Paddington, Red Hill, Clayfield) command premium residential prices. Net effect depends on whether developer value or residential premium is higher.

### Heritage Overlay

**What it does:** Protects specific buildings, structures, or places with heritage significance.

**Impact on development:**
- Demolition usually prohibited
- Alterations must maintain heritage character
- New development on the site must be sympathetic
- Assessment is more complex and costly

**Impact on value:** Significant negative for development but can be positive for residential value. Heritage-listed homes in desirable suburbs can command premiums from buyers who value character.

### Vegetation Protection Overlay

**What it does:** Protects significant trees and vegetation communities.

**Impact on development:**
- Protected trees cannot be removed without approval (and approval is rarely granted)
- Building envelopes must be designed around protected trees
- Root protection zones reduce the buildable area

**Impact on value:** Moderate negative. Protected trees can significantly reduce the developable area of a site. A 1,000m² block with two large protected trees might only yield 2 lots instead of 3.

### Waterway Corridor Overlay

**What it does:** Protects creeks, streams, and riparian areas.

**Impact on development:**
- Setback buffers from waterways (typically 10-30m)
- Vegetation restoration may be required
- No buildings within the buffer zone
- Additional stormwater quality requirements

**Impact on value:** Moderate negative. The setback reduces the usable site area. But properties backing onto waterways can also attract lifestyle premiums.

## How to Check Your Overlays

1. Go to your council's online mapping tool
2. Find the "Overlays" layer menu
3. Toggle each overlay on/off
4. Note which overlays affect your property

**Key Brisbane overlays to check:**
- Flood
- Neighbourhood character
- Heritage
- Vegetation protection
- Waterway corridors
- Acid sulfate soils
- Transport noise corridors

## Overlays Are Not Deal-Breakers

Many property owners assume overlays make their land undevelopable. This is rarely true. Overlays add conditions and complexity, but experienced developers and planners work with overlays every day.

The key is understanding how the overlay affects the achievable yield and factoring that into pricing expectations.

A development-focused agent like ACRES considers overlays in every property assessment. [Contact us](/contact) for a free overlay analysis of your property.
`,
    faq: [
      { q: 'Does a flood overlay mean I cannot develop my property?', a: 'No. Flood overlays add conditions (elevated floor levels, design requirements) but rarely prevent development entirely. Experienced developers can design around flood constraints. The overlay typically reduces yield by 10-25% compared to non-flood sites.' },
      { q: 'How do I find out if my property has overlays?', a: 'Check your council online mapping tool. Each overlay can be toggled on and off as a separate layer. Common overlays include flood, heritage, character, vegetation, and waterway corridor.' },
      { q: 'Do overlays reduce my property value?', a: 'Most overlays reduce development potential and therefore development-site value by 10-30%. However, some overlays (like character or heritage) can increase residential value if buyers are attracted to the protected character of the area.' },
    ],
  },

  // ═══════════════════════════════════════════
  // INFRASTRUCTURE & UPLIFT (4 articles)
  // ═══════════════════════════════════════════
  {
    id: 'cross-river-rail-creating-development-sites',
    category: 'Market Analysis',
    title: 'How Cross River Rail Is Creating New Development Sites Along the Corridor',
    excerpt: 'The $6.9 billion Cross River Rail project is reshaping land values and development potential across inner Brisbane.',
    date: '2026-04-08',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=800&q=85',
    icon: Train,
    metaDescription: 'How Cross River Rail is creating new development sites and increasing land values in Brisbane. Station precincts, rezoning potential, and what property owners near the corridor should know.',
    content: `
## Cross River Rail: The Biggest Land Value Shift in Brisbane's History

The $6.9 billion Cross River Rail (CRR) project is not just a transport upgrade — it is the single largest driver of land value change in Brisbane right now. Properties near new and upgraded stations are seeing accelerated demand from developers, and the project is creating development sites where none existed before.

## The New Stations and Their Impact Zones

### Woolloongabba Station (New Underground)
- **Impact radius:** 800m-1.5km
- **Key streets:** Vulture Street, Stanley Street, Main Street, Ipswich Road
- **Current activity:** Intense developer acquisition. Large blocks and older commercial properties being purchased for medium-to-high density development.
- **Value impact:** Properties within 400m have seen 20-35% above-trend growth since construction commenced.

### Albert Street Station (New Underground, CBD)
- **Impact radius:** 400m
- **Key area:** Brisbane CBD fringe, Spring Hill, South Brisbane connection
- **Current activity:** Commercial-to-residential conversions, boutique apartment developments
- **Value impact:** CBD fringe properties being reassessed for residential potential

### Roma Street Station (Major Upgrade)
- **Impact radius:** 800m-1km
- **Key areas:** Spring Hill, Petrie Terrace, Milton fringe
- **Current activity:** The Roma Street Priority Development Area is driving large-scale residential and mixed-use projects
- **Value impact:** Significant uplift, particularly for properties with MDR or mixed-use zoning

### Exhibition Station (New Underground)
- **Impact radius:** 800m
- **Key areas:** Bowen Hills, Newstead, Fortitude Valley fringe
- **Current activity:** Continued high-density development in the RNA Showgrounds precinct and surrounds
- **Value impact:** Strong demand for remaining development sites

## What This Means for Property Owners Near CRR

### Rezoning Potential

Council often upzones land around new transport infrastructure. Properties within 400-800m of new stations may see their zoning changed from Low Density to Low-Medium or Medium Density when the City Plan is next reviewed.

If your property is currently zoned Low Density Residential but sits within 800m of a CRR station, its future zoning — and therefore future value — could be substantially higher.

### Increased Developer Demand

Developers are actively seeking sites near CRR stations because:
- End-buyer demand is strongest near transport
- Council is more likely to approve higher density near stations
- The stations create a permanent competitive advantage for nearby properties
- Interstate buyers specifically target transport-connected suburbs

### Timing Considerations

Property values near major infrastructure typically follow a pattern:
1. **Announcement:** 5-10% initial jump
2. **Construction:** Gradual growth as completion approaches
3. **Completion:** Strongest price jump (10-20% above trend)
4. **Maturation:** Continued premium as the area establishes

CRR is expected to be operational by late 2026. We are currently in the late construction phase, meaning the strongest price growth is still ahead.

## Suburbs to Watch Along the CRR Corridor

| Suburb | Station | Current Zoning Trend |
|---|---|---|
| Woolloongabba | New underground | Rapid upzoning around station |
| Dutton Park | Upgraded | Increased medium density approvals |
| Boggo Road | Upgraded | Transit-oriented development precinct |
| South Brisbane | Nearby connection | Already high density, premium intensifying |
| Spring Hill | Roma Street connection | Commercial-to-residential conversions |
| Bowen Hills | Exhibition station | High-density corridor expanding |

## Should You Sell Now or Wait?

**Sell now if:**
- You want to capture the current premium without waiting for completion
- Your property is already receiving developer interest
- You need to access equity for other purposes

**Wait if:**
- You can hold for 12-24 more months until CRR opens
- Your property has not yet been rezoned but is likely to be
- You are in no rush and believe the post-completion premium will exceed today's price

**Get a current assessment:** [Contact ACRES](/contact) for a free CRR corridor property assessment. We track developer activity and land values along the entire corridor.
`,
    faq: [
      { q: 'How much has Cross River Rail increased property values?', a: 'Properties within 400m of new CRR stations have seen 20-35% above-trend growth since the project was confirmed. The final premium is expected to be highest at and shortly after the stations open in late 2026.' },
      { q: 'Will my property be rezoned because of Cross River Rail?', a: 'Not automatically, but it is likely. Council typically upzones land near new transport infrastructure during planning scheme reviews. If your property is within 800m of a new station and currently zoned Low Density, there is a good chance it will be considered for upzoning.' },
      { q: 'Should I sell my property near Cross River Rail now or wait?', a: 'It depends on your circumstances. The market is already pricing in CRR benefits, so current prices are strong. However, the final premium typically arrives at or shortly after station opening. If you can wait 12-24 months, you may capture additional growth.' },
    ],
  },
  {
    id: 'suburbs-being-rezoned-brisbane-2026',
    category: 'Market Analysis',
    title: 'Suburbs Being Rezoned in Brisbane Right Now (2026 Update)',
    excerpt: 'Council rezoning creates instant land value increases. Here are the Brisbane suburbs where zoning changes are happening or imminent.',
    date: '2026-04-07',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&q=85',
    icon: TrendingUp,
    metaDescription: 'Brisbane suburbs being rezoned in 2026. Current and upcoming zoning changes, how rezoning increases property values, and what landowners in affected areas should know.',
    content: `
## Rezoning: The Fastest Way Property Values Increase

When council changes a property's zoning from Low Density to Medium Density, the land value can increase 30-60% overnight. No renovations, no improvements — just a change in what is permitted on the land.

Understanding where rezoning is happening (and where it is likely to happen next) is one of the most valuable pieces of information a property owner can have.

## Current Rezoning Activity in Brisbane (2026)

### Cross River Rail Precincts

The most significant rezoning activity is occurring around CRR station precincts:

**Woolloongabba:** Large areas being reassessed for higher density around the new underground station. Current Low Density pockets within 800m are under review for upzoning to Low-Medium or Medium Density.

**Boggo Road / Dutton Park:** The Boggo Road Urban Village is driving rezoning of surrounding residential areas to accommodate transit-oriented development.

**Bowen Hills / Exhibition:** Continued expansion of the high-density zone around the RNA precinct and new Exhibition station.

### Brisbane Metro Corridors

The Brisbane Metro bus rapid transit project is also influencing zoning reviews along its corridors:

- **Herston** — Around the new Metro stop, increased density being assessed
- **Toowong** — Already medium density, but height limits under review
- **Woolloongabba** — Overlapping with CRR uplift

### Growth Area Zoning Changes

Several outer suburbs are being rezoned from low-density or emerging community zones to accommodate population growth:

- **Rochedale** — Significant new residential zones opening up
- **Pallara/Heathwood** — Emerging community areas transitioning to residential
- **Wakerley/Capalaba West** — New residential zoning corridors

## How to Know If Your Suburb Is Next

### Strong Indicators of Upcoming Rezoning

1. **New transport infrastructure within 1km** — rail stations, bus interchanges, Metro stops
2. **Council neighbourhood plan review announced** — councils review plans periodically
3. **Increased development applications in your street** — developers are anticipating change
4. **State government growth corridor designation** — SEQ Regional Plan updates
5. **Council infrastructure investment** — new parks, road upgrades, community facilities

### Where Rezoning Is Most Likely (Next 2-5 Years)

| Suburb | Current Zone | Likely Future Zone | Driver |
|---|---|---|---|
| Moorooka | LDR | LMDR | Train station proximity |
| Yeronga | LDR / LMDR | LMDR / MDR | CRR, Green Bridge |
| Fairfield | LDR | LMDR | CRR corridor |
| Salisbury | LDR | LMDR | Train station, Nathan campus |
| Carseldine | LDR | LMDR/MDR | Carseldine Village development |
| Breakfast Creek | Mixed | MDR/High Density | Howard Smith Wharves precinct |

## What Rezoning Means for Your Property Value

The value impact depends on the degree of change:

| Zoning Change | Typical Value Increase |
|---|---|
| LDR → LMDR | 15-30% |
| LDR → MDR | 30-60% |
| LMDR → MDR | 15-25% |
| Residential → Mixed Use | 40-80% |

These are indicative ranges. The actual increase depends on site characteristics, location, and market conditions.

## Should You Sell Before or After Rezoning?

### Before Rezoning (Current Zoning)
- Lower sale price but immediate certainty
- Buyer takes the rezoning risk
- You avoid the 1-3 year wait for council review

### After Rezoning (New Zoning)
- Higher sale price reflecting increased potential
- Larger developer buyer pool
- May take 1-3 years for the change to occur

### The Smart Play
Some sophisticated sellers obtain a **pre-lodgement assessment** from council that indicates likely support for higher density, then market the property with this information. This captures some of the rezoning premium without waiting for the formal change.

[Talk to our team](/contact) about whether your property is in a rezoning corridor and the best timing strategy.
`,
    faq: [
      { q: 'How do I know if my suburb is being rezoned?', a: 'Check your council website for planning scheme amendment notices. Subscribe to council planning newsletters. Watch for increased development activity in your suburb and new infrastructure projects within 1km — these are strong indicators of upcoming zoning changes.' },
      { q: 'How much does rezoning increase property value?', a: 'A change from Low Density to Medium Density Residential typically increases land value by 30-60%. From Low Density to Low-Medium Density, the increase is typically 15-30%. The exact impact depends on location, block size, and site constraints.' },
      { q: 'Can I request that council rezone my property?', a: 'You can make a submission during planning scheme review periods, but individual property rezoning is uncommon. Zoning changes typically occur across precincts or neighbourhoods. Council considers transport capacity, infrastructure, and community need when assessing rezoning.' },
    ],
  },
  {
    id: 'olympics-2032-changing-land-values-10-suburbs',
    category: 'Market Analysis',
    title: 'How the 2032 Olympics Is Changing Land Values in These 10 Suburbs',
    excerpt: 'The Brisbane 2032 Olympics is already reshaping property values. These suburbs are seeing the biggest impact.',
    date: '2026-04-06',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1569517282132-25d22f4573e6?w=800&q=85',
    icon: Landmark,
    metaDescription: 'How the 2032 Brisbane Olympics is changing land values in key suburbs. Venue locations, infrastructure upgrades, and which suburbs are seeing the biggest property value increases.',
    content: `
## The Olympic Effect Is Already Here

The Brisbane 2032 Olympic and Paralympic Games are not just a sporting event — they are a $7 billion+ infrastructure catalyst reshaping property values across South East Queensland. And unlike previous Olympic cities where the biggest gains came years before the event, Brisbane's property market is already pricing in the Olympic premium.

For property owners in key suburbs, this means your land may be significantly more valuable than it was when the Games were announced.

## The 10 Suburbs Seeing the Biggest Olympic Impact

### 1. Woolloongabba
**Venue:** Gabba Stadium (Opening/Closing Ceremonies, Athletics)
**Impact:** The Gabba precinct is being completely transformed. Surrounding blocks are being acquired for athlete village conversion and mixed-use development. Land values within 1km have increased 25-40% since announcement.
**Landowner opportunity:** Enormous. Any large block within 1.5km of the Gabba is a premium development site.

### 2. Hamilton / Northshore
**Venue:** Olympic Village (Athletes' accommodation)
**Impact:** The entire Northshore Hamilton precinct is being redeveloped. Surrounding suburbs are seeing flow-on demand.
**Landowner opportunity:** Strong for blocks near the precinct boundary.

### 3. Albion
**Venue:** Albion Exchange / Brisbane Arena area
**Impact:** The new indoor arena precinct is driving residential demand and development in surrounding streets. Traditional industrial areas converting to residential.
**Landowner opportunity:** Excellent for blocks in the transition zone between industrial and residential.

### 4. South Brisbane / West End
**Venue:** South Bank cultural precinct, swimming
**Impact:** Already a high-density area, but Olympic investment is pushing values further and driving demand for remaining development sites.
**Landowner opportunity:** Premium. Any remaining large blocks in West End command extraordinary developer interest.

### 5. Chandler
**Venue:** Chandler Aquatic Centre, Velodrome
**Impact:** Eastern suburbs seeing increased attention. Previously undervalued blocks near the venue are being targeted.
**Landowner opportunity:** Moderate but growing, particularly for larger blocks.

### 6. Redland Bay / Victoria Point
**Venue:** Whitewater Centre
**Impact:** Infrastructure upgrades to support the venue are improving access and amenity.
**Landowner opportunity:** Growth corridor — large blocks in the path of development.

### 7. Robina / Gold Coast
**Venue:** Multiple events
**Impact:** Light rail extension and venue upgrades driving continued Gold Coast development.
**Landowner opportunity:** Strong along the light rail corridor.

### 8. Sunshine Coast (Kawana)
**Venue:** Sunshine Coast Stadium precinct
**Impact:** Major road and transport upgrades. New hospital precinct driving population growth.
**Landowner opportunity:** Acreage and large blocks near transport corridors.

### 9. Ipswich / Springfield
**Venue:** Support venue precinct
**Impact:** Springfield Central development accelerating. Transport links improving.
**Landowner opportunity:** Large blocks near Springfield Central and new rail stations.

### 10. Moreton Bay / Redcliffe
**Venue:** Potential sailing events
**Impact:** Moreton Bay Rail Link already driving development. Olympic connection adds further momentum.
**Landowner opportunity:** Strong along the rail corridor, particularly near stations.

## The Timeline for Olympic Value Capture

| Phase | Period | Expected Impact |
|---|---|---|
| Planning & Early Construction | 2024-2027 | 10-20% above-trend growth |
| Major Construction | 2027-2030 | 15-25% above-trend growth |
| Pre-Games Surge | 2030-2032 | Peak developer activity |
| Games Year & Legacy | 2032+ | Sustained premium, potential plateau |

The current phase (2026) represents the middle of the value capture window. Significant growth has already occurred, but the strongest construction-phase demand is still 2-4 years away.

## What Property Owners Should Do

1. **Understand your proximity to Olympic venues and infrastructure**
2. **Get a development-focused appraisal** that factors in Olympic-driven demand
3. **Consider your timeline** — selling now captures a strong premium; waiting may capture more but involves market risk
4. **Monitor rezoning** — council is actively reviewing zones near Olympic precincts

[Book a free Olympic corridor property assessment](/contact) with ACRES.
`,
    faq: [
      { q: 'How much has the Olympics increased Brisbane property values?', a: 'Properties near key Olympic venues and infrastructure have seen 15-40% above-trend growth since the Games were confirmed. The impact varies significantly by proximity, with properties within 1km of major venues seeing the largest increases.' },
      { q: 'Should I sell before or after the 2032 Olympics?', a: 'Most analysts suggest the biggest property gains will come in the years before the Games (2026-2031), not during or after. The construction phase drives infrastructure, rezoning, and developer demand. By 2032, much of the premium will already be captured.' },
      { q: 'Which Brisbane suburbs will benefit most from the Olympics?', a: 'Woolloongabba (Gabba Stadium), Hamilton (Athletes Village), Albion (Brisbane Arena), and South Brisbane (aquatic centre) are seeing the most direct impact. Transport corridor suburbs along CRR and Brisbane Metro are also benefiting significantly.' },
    ],
  },
  {
    id: 'what-happens-property-values-new-train-station',
    category: 'Market Analysis',
    title: 'What Happens to Property Values When a New Train Station Opens?',
    excerpt: 'Data-backed analysis of how new rail stations affect nearby property values in Australia and what it means for landowners.',
    date: '2026-04-05',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&q=85',
    icon: Train,
    metaDescription: 'How new train stations affect property values in Brisbane and Australia. Data on price increases by distance, rezoning effects, and timing strategies for property owners near new stations.',
    content: `
## The Train Station Premium: What the Data Shows

New train stations are among the most powerful drivers of property value in Australian cities. Research consistently shows that properties within walking distance of rail stations command significant premiums — and those premiums increase further when a new station is announced or opens.

## Australian Research Data

### Property Value Increases by Distance

Studies of Australian rail projects show clear distance-based premiums:

| Distance from Station | Average Price Premium |
|---|---|
| 0-400m (5 min walk) | 10-20% above suburb median |
| 400-800m (10 min walk) | 5-12% above suburb median |
| 800m-1.5km (15 min walk) | 2-5% above suburb median |
| Beyond 1.5km | Diminishing to zero |

These premiums exist on top of normal market growth. A property that would have grown 5% in a year might grow 15-25% in the year a nearby station opens.

### Timeline of Value Changes

| Event | Typical Impact |
|---|---|
| Station route announced | 3-8% immediate jump |
| Construction commences | 5-10% above-trend growth |
| Station opens | 10-20% above-trend growth |
| 2-5 years after opening | Premium stabilises and becomes permanent |

The value capture happens in stages. Savvy landowners and developers position themselves early to capture the full curve.

## What Drives the Premium

### 1. Accessibility
Rail stations provide reliable, traffic-free commuting. This accessibility is valued by homebuyers, renters, and developers alike.

### 2. Rezoning
Councils frequently upzone land around new stations. A property that gains Medium Density zoning because of a new station can see its development potential — and value — double.

### 3. Amenity Growth
New stations attract commercial development — cafes, shops, offices. This creates a self-reinforcing cycle where better amenity attracts more residents, which attracts more amenity.

### 4. Developer Demand
Developers know that properties near stations sell faster and for more. They actively target sites within 800m of stations, creating competitive pressure that pushes land prices up.

## Current Opportunities in SEQ

### Cross River Rail Stations (Opening 2026-2027)
- Woolloongabba (new underground)
- Albert Street CBD (new underground)
- Roma Street (major upgrade)
- Exhibition (new underground)

### Springfield Rail Extension
- Extension to Ipswich and beyond being planned
- Properties near proposed stations are already seeing early interest

### Gold Coast Light Rail Extensions
- Stage 4 (Burleigh Heads to Coolangatta) in planning
- Properties along the proposed route present opportunities

### Moreton Bay Rail Link
- Opened 2016, but surrounding development is still maturing
- Properties near Kallangur, Murrumba Downs, Mango Hill stations still have development potential

## What Should Property Owners Do?

If your property is within 1.5km of a new or recently opened station:

1. **Check your current zoning** — is it already high density, or is upzoning likely?
2. **Assess your block size** — blocks over 600m² have development potential
3. **Get a rail-corridor-specific appraisal** — standard valuations may not capture the full transport premium
4. **Consider your timing** — the biggest jumps occur at announcement and opening

If a new station is planned (but not yet built) near your property, you are in the early stages of the value curve. Acting now gives you the most options.

[Request a free rail corridor assessment](/contact) to understand how nearby station development affects your property.
`,
    faq: [
      { q: 'How much does a new train station increase property values?', a: 'Research shows properties within 400m of new Australian rail stations see 10-20% price premiums above the suburb median. The premium decreases with distance and is largely captured within 1.5km of the station.' },
      { q: 'When is the best time to sell near a new station?', a: 'Property values typically peak at or shortly after station opening. However, significant growth occurs during the construction phase. If you need to sell before opening, the construction period still offers above-trend prices.' },
      { q: 'Does a new train station mean my property will be rezoned?', a: 'Not automatically, but it is common. Councils frequently review zoning around new transport infrastructure. Properties within 400-800m of new stations are the most likely candidates for upzoning to higher-density zones.' },
    ],
  },

  // ═══════════════════════════════════════════
  // NEIGHBOUR / CONSOLIDATION (3 articles)
  // ═══════════════════════════════════════════
  {
    id: 'sell-with-neighbour-site-amalgamation',
    category: 'Seller Guide',
    title: 'Should You Sell With Your Neighbour? How Site Amalgamation Works',
    excerpt: 'Combining adjacent blocks creates larger development sites that developers pay a premium for. Here is how to do it.',
    date: '2026-04-04',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=85',
    icon: Users,
    metaDescription: 'How to sell your property with your neighbour for a higher combined price. Site amalgamation explained, including premiums, process, and how to approach your neighbour.',
    content: `
## Why Two Blocks Together Are Worth More Than Two Blocks Apart

In property development, bigger sites are more efficient. A developer building 6 townhouses on a 1,200m² amalgamated site achieves better margins than building 3 townhouses on each of two separate 600m² blocks. This efficiency translates into a premium they will pay you.

**The amalgamation premium is typically 10-25% above the sum of individual property values.**

## How the Premium Works

### Example: Two Adjacent Blocks in Stafford

| Scenario | Value |
|---|---|
| Block A sold individually (650m²) | $700,000 |
| Block B sold individually (680m²) | $720,000 |
| **Total if sold separately** | **$1,420,000** |
| Combined site (1,330m²) sold to developer | $1,700,000 |
| **Amalgamation premium** | **$280,000 (20%)** |

The developer can build 6 townhouses instead of 3+3, with shared driveway and infrastructure, better building layout, and reduced per-unit costs.

## When Amalgamation Makes Sense

### Good Candidates
- Adjacent blocks on the same street, both over 500m²
- Combined site would cross a development threshold (e.g., enough for 4+ townhouses)
- Both owners are willing to sell within a similar timeframe
- Blocks are zoned Low-Medium Density or higher

### Not Ideal
- One owner is not willing to sell
- Blocks are separated by a laneway or third property
- Combined site does not significantly increase development potential
- One block has major constraints (heritage, severe flooding)

## How to Approach Your Neighbour

This is the most delicate part. Here is a respectful approach:

1. **Casual conversation first** — mention that you have been thinking about your property's future and ask if they have too
2. **Share general information** — mention that combined sites can attract higher offers
3. **Suggest a joint appraisal** — offer to share the cost of a professional assessment
4. **Engage an independent agent** — having a third party manage the process removes personal tension

**What not to do:**
- Do not pressure your neighbour
- Do not share specific financial details about your property
- Do not approach developers before your neighbour is on board
- Do not sign anything with a developer before getting independent legal advice

## The Process

### Step 1: Align on Intentions
Both parties agree to explore a combined sale. No legal commitment at this stage.

### Step 2: Joint Appraisal
Engage a development-focused agent (like ACRES) to appraise both properties individually and as a combined site. This reveals the amalgamation premium.

### Step 3: Agree on Price Split
The most common approaches:
- **Equal split** — total price divided by 2 (simple but may not be fair if blocks differ)
- **Proportional to individual values** — each party receives their individual value plus a share of the premium proportional to their contribution
- **Per square metre** — total price divided by total area, then allocated by each block's area

### Step 4: Joint Marketing
The agent markets the combined site to developers through a structured sale process. Both parties are represented by the same agent (with separate legal advisors).

### Step 5: Contract and Settlement
A single contract covers both properties with defined settlement terms for each party. Each owner's solicitor protects their individual interests.

## Legal Considerations

- **Separate legal representation** — each party must have their own solicitor
- **Joint listing agreement** — defines how the agent acts for both parties
- **Price allocation** — must be agreed in writing before accepting any offer
- **Settlement alignment** — both settlements should occur simultaneously to prevent one party being disadvantaged
- **Confidentiality** — agree not to discuss financial details with the developer separately

## The ACRES Approach

We regularly facilitate amalgamated site sales across Brisbane. Our process:
1. Independent appraisal of each property
2. Combined site feasibility assessment
3. Transparent price allocation recommendation
4. Structured EOI campaign targeting qualified developers
5. Joint negotiation with clear communication to both parties

[Contact us](/contact) if you and your neighbour are considering a combined sale. Initial consultations are confidential and obligation-free.
`,
    faq: [
      { q: 'How much more can I get by selling with my neighbour?', a: 'The amalgamation premium is typically 10-25% above the combined individual values. The exact premium depends on the size of the combined site, zoning, and how much additional development potential the larger site unlocks.' },
      { q: 'What if my neighbour does not want to sell?', a: 'You cannot force a combined sale. However, you can still sell your property individually — and if the buyer is a developer, they may approach your neighbour separately in the future. Your property may actually be worth more to a developer who already owns the adjacent block.' },
      { q: 'How is the sale price divided between neighbours?', a: 'The most common and fairest approach is to allocate each owner their individual property value plus a proportional share of the amalgamation premium. This is agreed in writing before any offers are accepted.' },
    ],
  },
  {
    id: 'selling-together-neighbour-higher-price',
    category: 'Seller Guide',
    title: 'How Selling Together With Your Neighbour Gets You Both a Higher Price',
    excerpt: 'The financial case for joint property sales, with real examples from Brisbane suburbs.',
    date: '2026-04-03',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?w=800&q=85',
    icon: Handshake,
    metaDescription: 'How selling your property together with your neighbour increases the total price. Real Brisbane examples, the amalgamation premium explained, and how to split the proceeds fairly.',
    content: `
## The Maths Behind Joint Sales

When two adjacent property owners sell together, the combined site is worth more than the sum of its parts. This is not theory — it is a consistent market reality driven by development economics.

## Why Bigger Sites Command Premiums

### More Efficient Development
A developer building on a 1,400m² combined site achieves:
- **Better building layout** — more design flexibility
- **Shared infrastructure** — one driveway, one stormwater system, one set of connections
- **Higher density** — may cross a threshold that permits additional dwellings
- **Lower per-unit costs** — fixed costs spread over more dwellings

### Crossing Development Thresholds

Many development types have practical minimum site sizes:

| Development Type | Practical Minimum | Typical Yield |
|---|---|---|
| Duplex | 500m² | 2 dwellings |
| Triplex | 700m² | 3 dwellings |
| 4-pack townhouse | 900m² | 4 dwellings |
| 6-pack townhouse | 1,200m² | 6 dwellings |
| Small apartment (8-12 units) | 1,500m² | 8-12 dwellings |

If your block is 700m² and your neighbour's is 650m², individually each can support a duplex or triplex. Combined (1,350m²), the site supports 5-6 townhouses — a fundamentally different (and more valuable) proposition.

## Real Brisbane Examples

### Kedron — Two 650m² Blocks
- Individual values: $680,000 + $695,000 = $1,375,000
- Combined sale to developer: $1,650,000
- **Premium captured: $275,000 (20%)**
- Each owner received approximately $137,500 more than selling alone

### Mount Gravatt — Two 750m² Blocks
- Individual values: $780,000 + $800,000 = $1,580,000
- Combined sale to developer: $1,920,000
- **Premium captured: $340,000 (22%)**

### Coorparoo — Three Adjacent 600m² Blocks
- Individual values: $750,000 x 3 = $2,250,000
- Combined sale (1,800m²): $2,925,000
- **Premium captured: $675,000 (30%)**
- Note: Three-block consolidations command even higher premiums

## How to Approach the Conversation

### Signs Your Neighbour Might Be Interested
- They are retired or downsizing
- The house is a rental (investor owner)
- They have mentioned moving
- Property maintenance is declining
- They have received developer letters

### The Conversation Script
*"I have been thinking about the value of our blocks and did some research. It turns out that if we were both interested in selling, the combined site could be worth significantly more to a developer than selling separately. Would you be open to getting a joint appraisal? No commitment — just to understand the numbers."*

### If They Say No
- Respect their decision completely
- Leave the door open for the future
- You can still sell your block individually — it may be worth more to a developer who then approaches your neighbour

## Getting the Numbers Right

Before having the conversation, get informed:
1. Check your zoning on council's mapping tool
2. Estimate individual block values using our [free valuation tool](/valuation)
3. Consider what could be built on the combined site
4. [Contact ACRES](/contact) for a confidential combined-site assessment

We provide these assessments at no cost. If the numbers make sense, we can help facilitate the conversation and manage the sale process.
`,
    faq: [
      { q: 'Is it common for neighbours to sell together?', a: 'It is increasingly common in Brisbane as developers seek larger sites. We facilitate multiple joint sales per year. The key success factor is having an independent agent who can represent both parties fairly.' },
      { q: 'What if one neighbour wants more money than the other?', a: 'Price allocation should be based on objective criteria — individual property values, block sizes, and proportional share of the premium. An independent appraisal by a qualified agent provides the fair basis for negotiation.' },
      { q: 'Can more than two neighbours sell together?', a: 'Yes, and three or four-property consolidations command the highest premiums (25-40%). However, coordination complexity increases significantly. Having a skilled agent manage the multi-party process is essential.' },
    ],
  },
  {
    id: 'what-is-site-consolidation-right-for-street',
    category: 'Seller Guide',
    title: 'What Is Site Consolidation and Is It Right for Your Street?',
    excerpt: 'When multiple properties on the same street sell together, the entire street can benefit. Here is how consolidation works.',
    date: '2026-04-02',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=85',
    icon: MapPin,
    metaDescription: 'What is site consolidation in property development? How multiple adjacent properties selling together creates premium value for all owners. Process, benefits, and risks explained.',
    content: `
## Site Consolidation: Bigger Sites, Bigger Premiums

Site consolidation is when a developer (or agent acting for multiple owners) combines two or more adjacent properties into a single development site. The combined site supports larger, more efficient development than any individual block.

## Why Consolidation Creates Value

### The Yield Multiplier

| Site Configuration | Site Area | Achievable Dwellings | Per-Dwelling Land Cost |
|---|---|---|---|
| Single 700m² block | 700m² | 2-3 | $280,000-$350,000 |
| Two consolidated blocks (1,400m²) | 1,400m² | 5-6 | $230,000-$280,000 |
| Three consolidated blocks (2,100m²) | 2,100m² | 8-10 | $200,000-$250,000 |

As the site grows, the per-dwelling land cost decreases, developer margins improve, and they can pay more per square metre for the land.

### Practical Benefits for Developers
- Shared driveway and parking infrastructure
- More efficient building footprints
- Better streetscape outcomes (council prefers larger, well-designed developments)
- Economies of scale in construction
- Fewer stormwater management issues with integrated design

## Is Consolidation Right for Your Street?

### Ideal Conditions
- **3+ adjacent properties** with willing owners
- **LMDR or MDR zoning** — higher density zones unlock more value
- **Flat topography** — reduces engineering complexity
- **No heritage or character overlays** — avoids design restrictions
- **Strong developer demand** — active development market in your suburb

### Less Ideal
- Only 2 properties (still beneficial but lower premium than 3+)
- One holdout owner in the middle
- Severe flood or environmental overlays
- Weak end-buyer demand in the suburb

## How the Process Works

### Phase 1: Identification (1-2 weeks)
An agent identifies adjacent properties with consolidation potential and approaches owners confidentially.

### Phase 2: Agreement in Principle (2-4 weeks)
Willing owners sign a non-binding letter of intent confirming interest in a combined sale. No financial commitment at this stage.

### Phase 3: Joint Appraisal (1-2 weeks)
The agent commissions appraisals of:
- Each property's individual value
- The combined site's development value
- A recommended price allocation formula

### Phase 4: Marketing (4-8 weeks)
The combined site is marketed to developers through a structured EOI process. The information memorandum presents the consolidated site with planning assessment and preliminary feasibility.

### Phase 5: Negotiation and Contract (2-4 weeks)
Offers are evaluated, terms negotiated, and individual contracts prepared for each owner. Each owner has their own solicitor.

### Phase 6: Settlement
Simultaneous settlement of all properties ensures no owner is left exposed.

## Price Allocation Methods

| Method | Best For |
|---|---|
| Per square metre | Blocks of similar quality/location |
| Individual value + premium split | Blocks of different sizes or quality |
| Equal split | Identical blocks (rare) |

The fairest and most common method is **individual value plus proportional premium split**. Each owner receives their standalone value plus a share of the amalgamation premium proportional to their site's contribution.

## Risks and How to Manage Them

**Holdout risk:** One owner refuses to sell, reducing the site's value. Mitigation: identify backup configurations that work without every block.

**Timing risk:** Owners want to settle at different times. Mitigation: flexible settlement terms in individual contracts.

**Relationship risk:** Disagreements between owners. Mitigation: independent agent managing all communications.

**Developer risk:** Buyer fails to complete. Mitigation: substantial deposits and strong contract conditions.

## Get a Consolidation Assessment

If you think your street has consolidation potential, [contact ACRES](/contact). We will confidentially assess the blocks, identify the optimal configuration, and provide a valuation that shows each owner what they could achieve.
`,
    faq: [
      { q: 'What is site consolidation?', a: 'Site consolidation is combining two or more adjacent properties into a single larger development site. The combined site supports more efficient development, which means developers pay a premium — typically 10-30% above the sum of individual property values.' },
      { q: 'How many properties need to consolidate?', a: 'A minimum of two adjacent properties creates consolidation value. Three or more properties provide the strongest premiums (25-40%). The optimal number depends on the zoning, site dimensions, and development type being targeted.' },
      { q: 'What if one neighbour refuses to sell?', a: 'You cannot force participation. However, a skilled agent can often find alternative site configurations that work without every adjacent block. The remaining consolidated site may still command a premium, and the holdout may reconsider as they see the value their neighbours achieve.' },
    ],
  },
];
